import type { AnchoredRow } from '../types/anchoredRowTypes'
import type {
  PiMatrixAgentThinkingLevel,
  PiStructuredGenerationExecution,
  PiStructuredGenerationRequest,
} from '../types/piMatrixAgent'
import { getCollationRowPrimaryTitle } from './ledgerSafeRowTitles'
import { preprocessLedgerText } from './ledgerTextPreprocessing'
import { sha256Hex } from './sha256'

export const ROW_TITLE_SUGGESTION_SCHEMA = 'variorum.row-title-suggestions.v1'
export const ROW_TITLE_GENERATION_RUN_SCHEMA_VERSION = 1 as const
export const ROW_TITLE_GENERATION_BATCH_SIZE = 12
export const ROW_TITLE_GENERATION_STORAGE_PREFIX = 'ledgerSafe.rowTitleGeneration.v1'

const MAX_READING_CHARACTERS = 1_200
const MAX_WITNESSES_PER_ROW = 12

export interface LedgerSafeRowTitleSuggestion {
  rowId: string
  title: string
  confidence: number | null
}

export interface LedgerSafeRowTitleGenerationBatch {
  id: string
  rowIds: string[]
  inputFingerprint: string
  generationId: string
  modelId: string
  thinkingLevel: PiMatrixAgentThinkingLevel
  suggestions: LedgerSafeRowTitleSuggestion[]
  status: 'ready' | 'applied' | 'dismissed'
  createdAt: string
  usageTotal: number
  elapsedMs: number
}

export interface LedgerSafeRowTitleGenerationRun {
  schemaVersion: typeof ROW_TITLE_GENERATION_RUN_SCHEMA_VERSION
  id: string
  chapterId: string
  rowIds: string[]
  nextRowIndex: number
  modelId: string
  thinkingLevel: PiMatrixAgentThinkingLevel
  batches: LedgerSafeRowTitleGenerationBatch[]
  createdAt: string
  updatedAt: string
}

const isRecord = (value: unknown): value is Record<string, unknown> => (
  Boolean(value) && typeof value === 'object' && !Array.isArray(value)
)

const clean = (value: unknown): string => String(value || '').replace(/\s+/g, ' ').trim()
const cleanReading = (value: unknown): string => preprocessLedgerText(String(value || '')).readingText

const stableRows = (rows: AnchoredRow[]): AnchoredRow[] => [...rows].sort((left, right) => (
  left.position - right.position || left.labelNumber - right.labelNumber || left.id.localeCompare(right.id)
))

export const fingerprintRowTitleGenerationInput = (rows: AnchoredRow[]): string => sha256Hex(JSON.stringify(
  stableRows(rows).map(row => ({
    id: row.id,
    position: row.position,
    labelPrefix: row.labelPrefix,
    labelNumber: row.labelNumber,
    currentTitle: getCollationRowPrimaryTitle(row),
    mappings: row.unitMappings.map(mapping => ({
      witnessId: mapping.witnessId,
      text: mapping.textSnapshot || '',
      excluded: Boolean(mapping.excluded),
    })),
  })),
))

const promptRow = (row: AnchoredRow) => ({
  row_id: row.id,
  label: `${row.labelPrefix}-${String(row.labelNumber).padStart(3, '0')}`,
  current_title: getCollationRowPrimaryTitle(row) || null,
  readings: row.unitMappings
    .filter(mapping => !mapping.excluded && cleanReading(mapping.textSnapshot))
    .slice(0, MAX_WITNESSES_PER_ROW)
    .map(mapping => ({
      witness_id: mapping.witnessId,
      text: cleanReading(mapping.textSnapshot).slice(0, MAX_READING_CHARACTERS),
    })),
})

export const buildRowTitleStructuredGenerationRequest = ({
  rows,
  modelId,
  thinkingLevel,
}: {
  rows: AnchoredRow[]
  modelId: string
  thinkingLevel: PiMatrixAgentThinkingLevel
}): PiStructuredGenerationRequest => {
  if (!rows.length) {
    throw new Error('Choose at least one row to title')
  }
  if (rows.length > ROW_TITLE_GENERATION_BATCH_SIZE) {
    throw new Error(`A title-generation batch may contain at most ${ROW_TITLE_GENERATION_BATCH_SIZE} rows`)
  }
  const rowIds = rows.map(row => row.id)
  return {
    schemaVersion: 'variorum.pi-structured-generation.v1',
    model: {
      provider: 'openai-codex',
      modelId,
    },
    thinkingLevel,
    systemPrompt: [
      'You assign concise scholarly English titles to rows in a manuscript collation.',
      'Return JSON only and obey the response schema exactly.',
      'Use the supplied row_id values verbatim.',
      'Describe the narrative or argumentative action represented by the readings.',
      'Do not mention witness identifiers, row numbers, manuscript variation, or uncertainty in a title.',
      'Return an object with schema_version and suggestions. Each suggestion must contain row_id and title.',
      'Confidence is optional; when supplied, it must be a number from 0 to 1.',
      'Prefer specific titles of 3 to 12 words. Return one suggestion for every input row.',
    ].join(' '),
    prompt: JSON.stringify({
      schema_version: ROW_TITLE_SUGGESTION_SCHEMA,
      requested_row_ids: rowIds,
      rows: rows.map(promptRow),
    }),
    responseSchema: {
      type: 'object',
      properties: {
        schema_version: { const: ROW_TITLE_SUGGESTION_SCHEMA },
        suggestions: {
          type: 'array',
          minItems: rows.length,
          maxItems: rows.length,
          items: {
            type: 'object',
            properties: {
              row_id: { type: 'string', enum: rowIds },
              title: { type: 'string' },
              confidence: {
                anyOf: [
                  { type: 'number' },
                  { type: 'null' },
                ],
              },
            },
            required: ['row_id', 'title'],
            additionalProperties: false,
          },
        },
      },
      required: ['schema_version', 'suggestions'],
      additionalProperties: false,
    },
    maxResponseCharacters: 20_000,
  }
}

export const parseRowTitleGenerationExecution = ({
  execution,
  rows,
}: {
  execution: PiStructuredGenerationExecution
  rows: AnchoredRow[]
}): LedgerSafeRowTitleSuggestion[] => {
  if (!execution.reply.schemaValid) {
    throw new Error(execution.reply.schemaError || 'Pi returned a row-title response that did not match its schema')
  }
  const payload = execution.reply.parsedJson
  if (!isRecord(payload) || payload.schema_version !== ROW_TITLE_SUGGESTION_SCHEMA || !Array.isArray(payload.suggestions)) {
    throw new Error('Pi returned an invalid row-title suggestion payload')
  }
  const requestedRowIds = rows.map(row => row.id)
  const requestedRowIdSet = new Set(requestedRowIds)
  const suggestionByRowId = new Map<string, LedgerSafeRowTitleSuggestion>()
  payload.suggestions.forEach((value, index) => {
    if (!isRecord(value)) {
      throw new Error(`Row-title suggestion ${index + 1} must be an object`)
    }
    const rowId = clean(value.row_id)
    const title = clean(value.title)
    const confidence = typeof value.confidence === 'number' && Number.isFinite(value.confidence)
      ? value.confidence
      : null
    if (!requestedRowIdSet.has(rowId)) {
      throw new Error(`Pi returned an unknown row id: ${rowId || 'missing'}`)
    }
    if (!title || title.length > 500) {
      throw new Error(`Pi returned an invalid title for ${rowId}`)
    }
    if (suggestionByRowId.has(rowId)) {
      throw new Error(`Pi returned more than one title for ${rowId}`)
    }
    suggestionByRowId.set(rowId, {
      rowId,
      title,
      confidence: confidence === null ? null : Math.max(0, Math.min(1, confidence)),
    })
  })
  const missingRowIds = requestedRowIds.filter(rowId => !suggestionByRowId.has(rowId))
  if (missingRowIds.length) {
    throw new Error(`Pi omitted ${missingRowIds.length} requested row title${missingRowIds.length === 1 ? '' : 's'}`)
  }
  return requestedRowIds.map(rowId => suggestionByRowId.get(rowId) as LedgerSafeRowTitleSuggestion)
}

const generateId = (prefix: string): string => (
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? `${prefix}:${crypto.randomUUID()}`
    : `${prefix}:${Date.now()}:${Math.random().toString(16).slice(2)}`
)

export const createLedgerSafeRowTitleGenerationRun = ({
  chapterId,
  rowIds,
  modelId,
  thinkingLevel,
  now = new Date().toISOString(),
}: {
  chapterId: string
  rowIds: string[]
  modelId: string
  thinkingLevel: PiMatrixAgentThinkingLevel
  now?: string
}): LedgerSafeRowTitleGenerationRun => ({
  schemaVersion: ROW_TITLE_GENERATION_RUN_SCHEMA_VERSION,
  id: generateId('row-title-run'),
  chapterId,
  rowIds: Array.from(new Set(rowIds)),
  nextRowIndex: 0,
  modelId,
  thinkingLevel,
  batches: [],
  createdAt: now,
  updatedAt: now,
})

export const appendLedgerSafeRowTitleGenerationBatch = ({
  run,
  rows,
  execution,
  suggestions,
  now = new Date().toISOString(),
}: {
  run: LedgerSafeRowTitleGenerationRun
  rows: AnchoredRow[]
  execution: PiStructuredGenerationExecution
  suggestions: LedgerSafeRowTitleSuggestion[]
  now?: string
}): LedgerSafeRowTitleGenerationRun => {
  const batch: LedgerSafeRowTitleGenerationBatch = {
    id: generateId('row-title-batch'),
    rowIds: rows.map(row => row.id),
    inputFingerprint: fingerprintRowTitleGenerationInput(rows),
    generationId: execution.generationId,
    modelId: execution.model.modelId,
    thinkingLevel: execution.thinkingLevel,
    suggestions,
    status: 'ready',
    createdAt: now,
    usageTotal: Number(execution.usage?.total) || 0,
    elapsedMs: Number(execution.timing?.elapsedMs) || 0,
  }
  return {
    ...run,
    nextRowIndex: run.nextRowIndex + rows.length,
    batches: [...run.batches, batch],
    updatedAt: now,
  }
}

export const updateLedgerSafeRowTitleGenerationBatchStatus = ({
  run,
  batchId,
  status,
  now = new Date().toISOString(),
}: {
  run: LedgerSafeRowTitleGenerationRun
  batchId: string
  status: LedgerSafeRowTitleGenerationBatch['status']
  now?: string
}): LedgerSafeRowTitleGenerationRun => ({
  ...run,
  batches: run.batches.map(batch => batch.id === batchId ? { ...batch, status } : batch),
  updatedAt: now,
})

const storageKey = (chapterId: string): string => `${ROW_TITLE_GENERATION_STORAGE_PREFIX}:${chapterId}`

export const saveLedgerSafeRowTitleGenerationRun = (
  run: LedgerSafeRowTitleGenerationRun,
  storage: Pick<Storage, 'setItem'> = window.localStorage,
): void => {
  storage.setItem(storageKey(run.chapterId), JSON.stringify(run))
}

export const loadLedgerSafeRowTitleGenerationRun = (
  chapterId: string,
  storage: Pick<Storage, 'getItem'> = window.localStorage,
): LedgerSafeRowTitleGenerationRun | null => {
  const raw = storage.getItem(storageKey(chapterId))
  if (!raw) return null
  try {
    const value = JSON.parse(raw) as LedgerSafeRowTitleGenerationRun
    if (
      value.schemaVersion !== ROW_TITLE_GENERATION_RUN_SCHEMA_VERSION
      || value.chapterId !== chapterId
      || !Array.isArray(value.rowIds)
      || !Array.isArray(value.batches)
    ) {
      return null
    }
    return value
  } catch {
    return null
  }
}

export const clearLedgerSafeRowTitleGenerationRun = (
  chapterId: string,
  storage: Pick<Storage, 'removeItem'> = window.localStorage,
): void => {
  storage.removeItem(storageKey(chapterId))
}
